import { useCallback, useEffect, useRef, useState } from 'react';
import { getComputedPropertyValueAsNumber } from '../../../../../../utils/dom/getComputedProperyValue';
import { hasWidth } from '../../../../../../utils/dom/hasWidth';
import { useBoolean } from '../../../../../../utils/hooks/useBoolean';
import { MediaManagerSDKAnyItemWithSectionInfo } from '../../../../../../utils/media-manager-sdk';

function elementHasWidth(el: HTMLElement): Promise<void> {
  if (hasWidth(el)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const rejectTimeout = 2000;

    const interval = setInterval(() => {
      if (!hasWidth(el)) {
        return;
      }

      clearInterval(interval);
      settled = true;
      resolve();
    }, 10);

    setTimeout(() => {
      if (!settled) {
        clearInterval(interval);
        settled = true;
        reject(new Error(`Waiting for element to have width bigger than 0 took longer then ${rejectTimeout} ms`));
      }
    }, rejectTimeout);
  });
}

async function calculateVisibleItemsCount(parentNode: HTMLElement, maxRowsToShow: number, items) {
  // iframe in which MediaManager is opened might be hidden with `display: none;`.
  // In that case `parentNode` will have 0 width and we need to wait until Media Manager shown.
  try {
    await elementHasWidth(parentNode);
  } catch {
    return 0;
  }

  const firstItem = parentNode.children[0];

  try {
    const containerPaddingLeft = getComputedPropertyValueAsNumber(parentNode, 'padding-left');
    const containerPaddingRight = getComputedPropertyValueAsNumber(parentNode, 'padding-right');
    const containerWidth = parentNode.clientWidth - containerPaddingLeft - containerPaddingRight;
    const firstItemWidth = firstItem.clientWidth;
    const itemsGap = getComputedPropertyValueAsNumber(firstItem, 'margin-right');
    const maxItemsInRow = Math.floor(containerWidth / (firstItemWidth + itemsGap));
    const maxItems = maxItemsInRow * maxRowsToShow;

    if (maxItems >= items.length) {
      return items.length;
    }

    return maxItems - 1;
  } catch (error) {
    return 0;
  }
}

export const useVisibleItems = ({
  items,
  maxRowsToShow,
}: {
  items: MediaManagerSDKAnyItemWithSectionInfo[];
  maxRowsToShow: number;
}) => {
  const { value: shouldShowAllItems, setTrue: showAllItems } = useBoolean(false);
  const [_visibleItemsCount, setVisibleItemsCount] = useState(null);

  const visibleItemsCount = shouldShowAllItems ? items.length : _visibleItemsCount;

  const itemsListRef = useRef(null);

  const windowResizeHandler = useCallback(async () => {
    if (!itemsListRef.current) {
      return;
    }

    const itemsListNode = itemsListRef.current.querySelector('[data-hook="generic-layout-items-list-wrapper"]');
    const visibleItemsCount = await calculateVisibleItemsCount(itemsListNode, maxRowsToShow, items);
    setVisibleItemsCount(visibleItemsCount);
  }, [items, maxRowsToShow]);

  useEffect(() => {
    windowResizeHandler();

    window.addEventListener('resize', windowResizeHandler);

    return () => {
      window.removeEventListener('resize', windowResizeHandler);
    };
  }, []);

  useEffect(() => {
    windowResizeHandler();
  }, [items.length]);

  return {
    itemsListRef,
    visibleItemsCount,
    visibleItems: visibleItemsCount ? items.slice(0, visibleItemsCount) : items,
    shouldShowAllItems,
    showAllItems,
  };
};
